import { useState, useEffect, useMemo, useCallback } from 'react'
import { useRoles } from '../context/RolesContext'
import { OwnerTable, type ColumnDef } from '../kit/OwnerTable'
import { OwnerEmptyScaffold } from '../kit/OwnerEmptyScaffold'
import { OwnerFirmConfirm } from '../kit/OwnerConfirm'
import { navigateOwner, type OwnerRoute } from '../ownerRouter'
import { useNls } from '../../../nls/nlsProvider'
import type { NlsKey } from '../../../nls/strings_nls'
import type {
  SupportTicket,
  SupportTicketPriority,
  SupportTicketStatus,
} from '../../../types/supportTicket'
import {
  listSupportTickets,
  getSupportSavedViewCounts,
  getSupportTicketById,
  updateSupportTicket,
  type SupportSavedViewId,
  type SupportSavedViewCounts,
  type SupportTicketCursor,
} from '../../../services/supportTicketsService'
import { formatSlaRemaining, calculateRemainingSlaMs } from '../../../utils/supportSla'
import { useSupportAccessReason } from '../shell/SupportAccessReasonDialog'
import { OwnerSupportDetail } from './OwnerSupportDetail'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import './ownerSupport.css'

const getTimestampMs = (val: unknown): number => {
  if (typeof val === 'number') return val
  if (val && typeof val === 'object' && 'toMillis' in val && typeof (val as { toMillis: unknown }).toMillis === 'function') {
    return (val as { toMillis: () => number }).toMillis()
  }
  if (typeof val === 'string') {
    const t = new Date(val).getTime()
    return Number.isNaN(t) ? 0 : t
  }
  return 0
}

interface ViewItem {
  id: SupportSavedViewId
  labelKey: NlsKey
  defaultLabel: string
}

const PREBUILT_VIEWS: ViewItem[] = [
  { id: 'open', labelKey: 'NLSOwnerConsoleSupportViewOpen', defaultLabel: 'Open' },
  { id: 'awaiting-me', labelKey: 'NLSOwnerConsoleSupportViewAwaitingMe', defaultLabel: 'Awaiting me' },
  { id: 'snoozed', labelKey: 'NLSOwnerConsoleSupportViewSnoozed', defaultLabel: 'Snoozed' },
  { id: 'breached-sla', labelKey: 'NLSOwnerConsoleSupportViewBreachedSla', defaultLabel: 'Breached SLA' },
  { id: 'bundled', labelKey: 'NLSOwnerConsoleSupportViewBundled', defaultLabel: 'Bundled' },
  { id: 'recently-resolved', labelKey: 'NLSOwnerConsoleSupportViewRecentlyResolved', defaultLabel: 'Recently resolved' },
  { id: 'all', labelKey: 'NLSOwnerConsoleSupportViewAll', defaultLabel: 'All' },
]

export const OwnerSupportPanel = ({ route }: { route: OwnerRoute }) => {
  const { t } = useNls()
  const { role, can } = useRoles()
  const { ensureAccessReason } = useSupportAccessReason()

  const isAuthorized = role === 'founder' || role === 'support' || can('logs:read')

  // URL Query parsing
  const currentQuery = useMemo(() => route.query || {}, [route.query])
  const activeSavedView: SupportSavedViewId = (currentQuery.savedView as SupportSavedViewId) || 'open'
  const activeStatusFilter = useMemo(
    () => (currentQuery.status ? (currentQuery.status.split(',') as SupportTicketStatus[]) : undefined),
    [currentQuery.status],
  )
  const activePriorityFilter = useMemo(
    () => (currentQuery.priority ? (currentQuery.priority.split(',') as SupportTicketPriority[]) : undefined),
    [currentQuery.priority],
  )
  const activeHasBundle = currentQuery.hasBundle === 'true' ? true : currentQuery.hasBundle === 'false' ? false : undefined
  const activeHasAttachments = currentQuery.hasAttachments === 'true' ? true : currentQuery.hasAttachments === 'false' ? false : undefined
  const activeSearch = currentQuery.q || ''

  const selectedTicketId = route.view === 'ticket-detail' ? route.ticketId : currentQuery.selectedId || null

  // State
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [cursor, setCursor] = useState<SupportTicketCursor | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [viewCounts, setViewCounts] = useState<SupportSavedViewCounts>({
    open: { count: 0, hasBreach: false },
    'awaiting-me': { count: 0, hasBreach: false },
    snoozed: { count: 0, hasBreach: false },
    'breached-sla': { count: 0, hasBreach: false },
    bundled: { count: 0, hasBreach: false },
    'recently-resolved': { count: 0, hasBreach: false },
    all: { count: 0, hasBreach: false },
  })
  const [directTicket, setDirectTicket] = useState<SupportTicket | null>(null)

  // Selection & bulk action states
  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<string>>(new Set())
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0)
  const [showBulkCloseConfirm, setShowBulkCloseConfirm] = useState(false)
  const [showBulkTagModal, setShowBulkTagModal] = useState(false)
  const [bulkTagInput, setBulkTagInput] = useState('')
  const [showBulkSnoozeMenu, setShowBulkSnoozeMenu] = useState(false)
  const [showBulkPriorityMenu, setShowBulkPriorityMenu] = useState(false)
  const [showJumpModal, setShowJumpModal] = useState(false)
  const [jumpTicketIdInput, setJumpTicketIdInput] = useState('')
  const [isBulkExecuting, setIsBulkExecuting] = useState(false)

  // URL patcher
  const updateQuery = useCallback(
    (patch: Record<string, string | undefined>) => {
      const nextQuery: Record<string, string | undefined> = { ...currentQuery, ...patch }
      Object.keys(nextQuery).forEach((k) => {
        if (nextQuery[k] === undefined || nextQuery[k] === '') {
          delete nextQuery[k]
        }
      })
      navigateOwner({ view: 'inbox', query: nextQuery })
    },
    [currentQuery],
  )

  // Fetch view counts
  const refreshCounts = useCallback(async () => {
    if (!isAuthorized) return
    const counts = await getSupportSavedViewCounts()
    setViewCounts(counts)
  }, [isAuthorized])

  useEffect(() => {
    if (!isAuthorized) return
    let active = true
    getSupportSavedViewCounts().then((counts) => {
      if (active) setViewCounts(counts)
    })
    return () => {
      active = false
    }
  }, [isAuthorized])

  const filterKey = `${activeSavedView}|${(activeStatusFilter || []).join(',')}|${(activePriorityFilter || []).join(',')}|${activeHasBundle}|${activeHasAttachments}|${activeSearch}`
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey)
    setCursor(null)
    setHasMore(false)
    setLoading(true)
    setFetchError(null)
  }

  useEffect(() => {
    if (!isAuthorized) return
    let cancelled = false
    void listSupportTickets(
      {
        savedView: activeSavedView,
        status: activeStatusFilter,
        priority: activePriorityFilter,
        hasBundle: activeHasBundle,
        hasAttachments: activeHasAttachments,
        q: activeSearch,
      },
      null,
    )
      .then((result) => {
        if (cancelled) return
        setTickets(result.tickets)
        setCursor(result.nextCursor)
        setHasMore(result.hasMore)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setFetchError(err instanceof Error ? err.message : 'Failed to query support tickets.')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isAuthorized, activeSavedView, activeStatusFilter, activePriorityFilter, activeHasBundle, activeHasAttachments, activeSearch])

  const handleLoadMore = useCallback(async () => {
    if (!isAuthorized || loading || !cursor) return
    setLoading(true)
    setFetchError(null)
    try {
      const result = await listSupportTickets(
        {
          savedView: activeSavedView,
          status: activeStatusFilter,
          priority: activePriorityFilter,
          hasBundle: activeHasBundle,
          hasAttachments: activeHasAttachments,
          q: activeSearch,
        },
        cursor,
      )
      setTickets((prev) => [...prev, ...result.tickets])
      setCursor(result.nextCursor)
      setHasMore(result.hasMore)
    } catch (err: unknown) {
      setFetchError(err instanceof Error ? err.message : 'Failed to query support tickets.')
    } finally {
      setLoading(false)
    }
  }, [isAuthorized, loading, cursor, activeSavedView, activeStatusFilter, activePriorityFilter, activeHasBundle, activeHasAttachments, activeSearch])

  const [prevSelectedTicketId, setPrevSelectedTicketId] = useState<string | null>(selectedTicketId)
  if (prevSelectedTicketId !== selectedTicketId) {
    setPrevSelectedTicketId(selectedTicketId)
    setDirectTicket(null)
  }

  // Direct fetch for selected ticket if not in memory
  useEffect(() => {
    if (!selectedTicketId || tickets.some((t) => t.id === selectedTicketId)) {
      return
    }
    let cancelled = false
    void (async () => {
      const reason = await ensureAccessReason({
        key: `SUPPORT_TICKET:${selectedTicketId}`,
        defaultCode: 'SUPPORT_TICKET',
        defaultTicketId: selectedTicketId,
      })
      if (!reason || cancelled) return
      getSupportTicketById(selectedTicketId, reason)
        .then((ticket) => {
          if (!cancelled && ticket) {
            setDirectTicket(ticket)
          }
        })
        .catch(() => {})
    })()
    return () => {
      cancelled = true
    }
  }, [selectedTicketId, tickets, ensureAccessReason])

  const selectedTicket = useMemo(() => {
    if (!selectedTicketId) return null
    return tickets.find((t) => t.id === selectedTicketId) || directTicket || null
  }, [tickets, selectedTicketId, directTicket])

  const copyTicketId = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(id)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 1500)
  }, [])

  // Bulk actions handlers
  const handleBulkClose = async () => {
    setIsBulkExecuting(true)
    try {
      await Promise.all(
        Array.from(selectedTicketIds).map((id) =>
          updateSupportTicket({ ticketId: id, status: 'resolved' })
        )
      )
      setTickets((prev) =>
        prev.map((t) => (selectedTicketIds.has(t.id) ? { ...t, status: 'resolved' } : t))
      )
      setSelectedTicketIds(new Set())
      setShowBulkCloseConfirm(false)
      refreshCounts()
    } catch (err: unknown) {
      alert(`Bulk close failed: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setIsBulkExecuting(false)
    }
  }

  const handleBulkTag = async (tag: string) => {
    const cleanTag = tag.trim().toLowerCase()
    if (!cleanTag) return
    setIsBulkExecuting(true)
    try {
      await Promise.all(
        Array.from(selectedTicketIds).map((id) => {
          const t = tickets.find((tk) => tk.id === id)
          const currentTags = t?.tags || []
          if (currentTags.includes(cleanTag)) return Promise.resolve()
          return updateSupportTicket({ ticketId: id, tags: [...currentTags, cleanTag] })
        })
      )
      setTickets((prev) =>
        prev.map((t) => {
          if (!selectedTicketIds.has(t.id)) return t
          const currentTags = t.tags || []
          return currentTags.includes(cleanTag) ? t : { ...t, tags: [...currentTags, cleanTag] }
        })
      )
      setSelectedTicketIds(new Set())
      setShowBulkTagModal(false)
      setBulkTagInput('')
    } catch (err: unknown) {
      alert(`Bulk tag failed: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setIsBulkExecuting(false)
    }
  }

  const handleBulkSnooze = async (durationHours: number) => {
    const snoozedUntil = new Date(Date.now() + durationHours * 3600 * 1000).toISOString()
    setIsBulkExecuting(true)
    try {
      await Promise.all(
        Array.from(selectedTicketIds).map((id) =>
          updateSupportTicket({ ticketId: id, status: 'snoozed', snoozedUntil })
        )
      )
      setTickets((prev) =>
        prev.map((t) =>
          selectedTicketIds.has(t.id)
            ? { ...t, status: 'snoozed', snoozedUntil }
            : t
        )
      )
      setSelectedTicketIds(new Set())
      setShowBulkSnoozeMenu(false)
      refreshCounts()
    } catch (err: unknown) {
      alert(`Bulk snooze failed: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setIsBulkExecuting(false)
    }
  }

  const handleBulkPriority = async (priority: SupportTicketPriority) => {
    setIsBulkExecuting(true)
    try {
      await Promise.all(
        Array.from(selectedTicketIds).map((id) =>
          updateSupportTicket({ ticketId: id, priority })
        )
      )
      setTickets((prev) =>
        prev.map((t) => (selectedTicketIds.has(t.id) ? { ...t, priority } : t))
      )
      setSelectedTicketIds(new Set())
      setShowBulkPriorityMenu(false)
    } catch (err: unknown) {
      alert(`Bulk priority failed: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setIsBulkExecuting(false)
    }
  }

  const handleJumpToTicket = (id: string) => {
    const cleanId = id.trim().replace(/^#/, '')
    if (!cleanId) return
    setShowJumpModal(false)
    setJumpTicketIdInput('')
    updateQuery({ selectedId: cleanId })
  }

  // Keyboard navigation & shortcuts (j, k, o, Enter, s, t, p, #) dispatched from OwnerConsole
  useEffect(() => {
    const handleSupportKey = (e: Event) => {
      const customEvent = e as CustomEvent<{ key: string }>
      const key = customEvent.detail?.key
      if (!key) return

      if (key === 'j') {
        setHighlightedIndex((prev) => Math.min(tickets.length - 1, prev + 1))
      } else if (key === 'k') {
        setHighlightedIndex((prev) => Math.max(0, prev - 1))
      } else if (key === 'o' || key === 'Enter') {
        if (tickets[highlightedIndex]) {
          updateQuery({ selectedId: tickets[highlightedIndex].id })
        }
      } else if (key === 's') {
        if (selectedTicketIds.size > 0) {
          setShowBulkSnoozeMenu(true)
        } else if (tickets[highlightedIndex]) {
          setSelectedTicketIds(new Set([tickets[highlightedIndex].id]))
          setShowBulkSnoozeMenu(true)
        }
      } else if (key === 't') {
        if (selectedTicketIds.size > 0) {
          setShowBulkTagModal(true)
        } else if (tickets[highlightedIndex]) {
          setSelectedTicketIds(new Set([tickets[highlightedIndex].id]))
          setShowBulkTagModal(true)
        }
      } else if (key === 'p') {
        if (selectedTicketIds.size > 0) {
          setShowBulkPriorityMenu(true)
        } else if (tickets[highlightedIndex]) {
          setSelectedTicketIds(new Set([tickets[highlightedIndex].id]))
          setShowBulkPriorityMenu(true)
        }
      } else if (key === '#') {
        setShowJumpModal(true)
      }
    }

    window.addEventListener('owner-support-key', handleSupportKey)
    return () => window.removeEventListener('owner-support-key', handleSupportKey)
  }, [tickets, highlightedIndex, selectedTicketIds, updateQuery])

  // Columns definition for OwnerTable
  const columns: ColumnDef<SupportTicket>[] = useMemo(
    () => [
      {
        key: 'select',
        header: '',
        width: 40,
        render: (row) => (
          <input
            type="checkbox"
            className="ownerSupportCheckbox"
            checked={selectedTicketIds.has(row.id)}
            onChange={(e) => {
              e.stopPropagation()
              setSelectedTicketIds((prev) => {
                const next = new Set(prev)
                if (next.has(row.id)) next.delete(row.id)
                else next.add(row.id)
                return next
              })
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ticket ${row.title}`}
          />
        ),
      },
      {
        key: 'ticket',
        header: 'Ticket',
        render: (row, index) => {
          const createdAtStr = row.createdAt
            ? new Date(getTimestampMs(row.createdAt)).toLocaleDateString()
            : ''
          const isHighlighted = index === highlightedIndex
          return (
            <div
              style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}
              className={isHighlighted ? 'ownerTableRow--highlighted' : ''}
            >
              <span
                className={`ownerPriorityPip ownerPriorityPip--${row.priority}`}
                title={`Priority ${row.priority}`}
                style={{ marginTop: 5 }}
              />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="ownerTicketSubject" title={row.title}>
                  {row.title || 'Untitled ticket'}
                </div>
                <div className="ownerTicketMetaSecondary">
                  <span>{row.reporterEmail || 'anonymous'}</span>
                  <span>•</span>
                  <span>{createdAtStr}</span>
                </div>
                {row.tags && row.tags.length > 0 && (
                  <div className="ownerSupportTagsRow">
                    {row.tags.map((tag) => (
                      <span key={tag} className="ownerSupportTagChip">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        },
      },
      {
        key: 'id',
        header: 'ID',
        width: 140,
        render: (row) => {
          const isCopied = copiedId === row.id
          return (
            <button
              type="button"
              className="ownerTicketIdCopy"
              onClick={(e) => copyTicketId(row.id, e)}
              title="Click to copy ticket ID"
            >
              #{row.id.slice(0, 10)}
              {isCopied ? (
                <CheckRoundedIcon style={{ fontSize: 13, color: '#10b981' }} />
              ) : (
                <ContentCopyRoundedIcon style={{ fontSize: 13 }} />
              )}
            </button>
          )
        },
      },
      {
        key: 'sla',
        header: 'SLA',
        width: 130,
        render: (row) => {
          const deadlineMs = getTimestampMs(row.slaDeadline)
          const pausedAtMs = row.slaPausedAt ? getTimestampMs(row.slaPausedAt) : null
          const remainingMs = calculateRemainingSlaMs(deadlineMs, pausedAtMs)
          const { label, isBreached } = formatSlaRemaining(remainingMs)

          return (
            <span className={`ownerSlaPill ${isBreached ? 'ownerSlaPill--breached' : ''}`}>
              {row.status === 'waiting-on-reporter' ? `⏸ ${label}` : label}
            </span>
          )
        },
      },
    ],
    [copiedId, copyTicketId, selectedTicketIds, highlightedIndex],
  )

  if (!isAuthorized) {
    return (
      <OwnerEmptyScaffold
        title="Access Denied"
        body="Support inbox access requires founder or support role."
      />
    )
  }

  return (
    <div className="ownerSupportContainer" data-testid="owner-support-panel">
      {/* 1. Left Rail: Saved Views */}
      <nav className="ownerSupportRail" aria-label="Support saved views">
        <div className="ownerSupportRailHeader">
          {t('NLSOwnerConsoleSupportViewsTitle')}
        </div>
        {PREBUILT_VIEWS.map((view) => {
          const countInfo = viewCounts[view.id] || { count: 0, hasBreach: false }
          const isActive = activeSavedView === view.id

          return (
            <button
              key={view.id}
              type="button"
              className={`ownerSupportRailItem ${isActive ? 'ownerSupportRailItem--active' : ''}`}
              onClick={() => updateQuery({ savedView: view.id, selectedId: undefined })}
            >
              <span>{t(view.labelKey) || view.defaultLabel}</span>
              <span
                className={`ownerSupportRailBadge ${
                  countInfo.hasBreach ? 'ownerSupportRailBadge--breach' : ''
                }`}
              >
                {countInfo.count}
              </span>
            </button>
          )
        })}
      </nav>

      {/* 2. Center Pane: Ticket List */}
      <section className="ownerSupportListPane" aria-label="Support tickets list">
        <header className="ownerSupportListHeader">
          <div className="ownerSupportTitleRow">
            <h1 className="ownerSupportTitle">{t('NLSOwnerConsoleSupportTitle')}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="search"
                className="ownerTableSearchInput"
                placeholder="Search tickets..."
                value={activeSearch}
                onChange={(e) => updateQuery({ q: e.target.value })}
                style={{ width: 220 }}
              />
            </div>
          </div>

          {/* Filter Chips */}
          <div className="ownerSupportFilterChipsRow">
            {/* Priority Filter Chips */}
            {(['P0', 'P1', 'P2', 'P3'] as SupportTicketPriority[]).map((p) => {
              const isSelected = activePriorityFilter?.includes(p)
              return (
                <button
                  key={p}
                  type="button"
                  className={`ownerSupportFilterChip ${isSelected ? 'ownerSupportFilterChip--active' : ''}`}
                  onClick={() => {
                    if (isSelected) {
                      const next = activePriorityFilter?.filter((item) => item !== p)
                      updateQuery({ priority: next && next.length > 0 ? next.join(',') : undefined })
                    } else {
                      const next = [...(activePriorityFilter || []), p]
                      updateQuery({ priority: next.join(',') })
                    }
                  }}
                >
                  <span className={`ownerPriorityPip ownerPriorityPip--${p}`} />
                  {p}
                </button>
              )
            })}

            {/* Has Bundle Filter Chip */}
            <button
              type="button"
              className={`ownerSupportFilterChip ${activeHasBundle === true ? 'ownerSupportFilterChip--active' : ''}`}
              onClick={() => updateQuery({ hasBundle: activeHasBundle === true ? undefined : 'true' })}
            >
              {t('NLSOwnerConsoleSupportFilterBundle')}
            </button>

            {/* Has Attachments Filter Chip */}
            <button
              type="button"
              className={`ownerSupportFilterChip ${activeHasAttachments === true ? 'ownerSupportFilterChip--active' : ''}`}
              onClick={() => updateQuery({ hasAttachments: activeHasAttachments === true ? undefined : 'true' })}
            >
              {t('NLSOwnerConsoleSupportFilterAttachments')}
            </button>

            {/* Registered Seams for Future Phases */}
            {/* [SEAM: Phase 1b People Hub] reporter cohort filter */}
            {/* [SEAM: Phase 1c Workspaces Hub] workspace plan filter */}
          </div>
        </header>

        <div className="ownerSupportTableWrapper">
          {fetchError ? (
            <OwnerEmptyScaffold
              title="Query Error"
              body={fetchError}
            />
          ) : tickets.length === 0 && !loading ? (
            <OwnerEmptyScaffold
              title={t('NLSOwnerConsoleSupportEmptyTitle')}
              body={t('NLSOwnerConsoleSupportEmptyDesc')}
            />
          ) : (
            <>
              <OwnerTable<SupportTicket>
                columns={columns}
                data={tickets}
                viewKey="owner-support-tickets"
                getRowId={(r) => r.id}
                onRowClick={(row) => updateQuery({ selectedId: row.id })}
                emptyState={<div>No tickets</div>}
              />
              {hasMore && (
                <button
                  type="button"
                  className="ownerLogsActionBtn"
                  style={{ alignSelf: 'center', marginTop: 12 }}
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? 'Loading…' : 'Load more tickets'}
                </button>
              )}
            </>
          )}
        </div>

        {/* Floating Bulk Action Bar */}
        {selectedTicketIds.size > 0 && (
          <div className="ownerSupportBulkBar" data-testid="owner-support-bulk-bar">
            <div className="ownerSupportBulkBarCount">
              <strong>{selectedTicketIds.size}</strong> {selectedTicketIds.size === 1 ? 'ticket' : 'tickets'} selected
            </div>
            <div className="ownerSupportBulkBarActions">
              <button
                type="button"
                className="ownerSupportBulkBtn"
                onClick={() => setShowBulkTagModal(true)}
                disabled={isBulkExecuting}
              >
                + Tag
              </button>
              <button
                type="button"
                className="ownerSupportBulkBtn"
                onClick={() => setShowBulkSnoozeMenu(true)}
                disabled={isBulkExecuting}
              >
                Snooze
              </button>
              <button
                type="button"
                className="ownerSupportBulkBtn"
                onClick={() => setShowBulkPriorityMenu(true)}
                disabled={isBulkExecuting}
              >
                Priority
              </button>
              <button
                type="button"
                className="ownerSupportBulkBtn ownerSupportBulkBtn--danger"
                onClick={() => setShowBulkCloseConfirm(true)}
                disabled={isBulkExecuting}
              >
                Close ({selectedTicketIds.size})
              </button>
              <button
                type="button"
                className="ownerSupportBulkBtn ownerSupportBulkBtn--ghost"
                onClick={() => setSelectedTicketIds(new Set())}
                disabled={isBulkExecuting}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </section>

      {/* 3. Right Pane: Detail View */}
      {selectedTicket ? (
        <OwnerSupportDetail
          ticket={selectedTicket}
          onTicketUpdated={(updated) => {
            setTickets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
            if (directTicket?.id === updated.id) setDirectTicket(updated)
            refreshCounts()
          }}
          onClose={() => updateQuery({ selectedId: undefined })}
        />
      ) : (
        <aside
          className="ownerSupportDetailPane"
          aria-label="Support ticket details"
          data-testid="owner-support-detail-pane"
        >
          <OwnerEmptyScaffold
            title={t('NLSOwnerConsoleSupportEmptyDetailTitle')}
            body={t('NLSOwnerConsoleSupportEmptyDetailDesc')}
          />
        </aside>
      )}

      {/* Bulk Modals & Confirmations */}
      <OwnerFirmConfirm
        isOpen={showBulkCloseConfirm}
        title={`Close ${selectedTicketIds.size} tickets?`}
        description={`This will mark ${selectedTicketIds.size} tickets as resolved and notify the reporters.`}
        confirmLabel={`Close ${selectedTicketIds.size} tickets`}
        onConfirm={handleBulkClose}
        onClose={() => setShowBulkCloseConfirm(false)}
      />

      {showBulkTagModal && (
        <div className="ownerModalOverlay" onClick={() => setShowBulkTagModal(false)}>
          <div className="ownerModalDialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <div className="ownerModalHeader">
              <h3>Tag {selectedTicketIds.size} tickets</h3>
            </div>
            <div className="ownerModalBody">
              <input
                type="text"
                className="ownerSupportTagInput"
                placeholder="Enter tag name..."
                value={bulkTagInput}
                onChange={(e) => setBulkTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleBulkTag(bulkTagInput)
                  if (e.key === 'Escape') setShowBulkTagModal(false)
                }}
                autoFocus
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="ownerModalActions">
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--default"
                onClick={() => setShowBulkTagModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--primary"
                onClick={() => handleBulkTag(bulkTagInput)}
                disabled={!bulkTagInput.trim()}
              >
                Apply tag
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkSnoozeMenu && (
        <div className="ownerModalOverlay" onClick={() => setShowBulkSnoozeMenu(false)}>
          <div className="ownerModalDialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 320 }}>
            <div className="ownerModalHeader">
              <h3>Snooze {selectedTicketIds.size} tickets</h3>
            </div>
            <div className="ownerModalBody" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                className="ownerSupportSnoozeOption"
                onClick={() => handleBulkSnooze(1)}
              >
                1 hour
              </button>
              <button
                type="button"
                className="ownerSupportSnoozeOption"
                onClick={() => handleBulkSnooze(4)}
              >
                4 hours
              </button>
              <button
                type="button"
                className="ownerSupportSnoozeOption"
                onClick={() => handleBulkSnooze(24)}
              >
                Tomorrow (24 hours)
              </button>
              <button
                type="button"
                className="ownerSupportSnoozeOption"
                onClick={() => handleBulkSnooze(168)}
              >
                Next week (7 days)
              </button>
            </div>
            <div className="ownerModalActions">
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--default"
                onClick={() => setShowBulkSnoozeMenu(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkPriorityMenu && (
        <div className="ownerModalOverlay" onClick={() => setShowBulkPriorityMenu(false)}>
          <div className="ownerModalDialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 320 }}>
            <div className="ownerModalHeader">
              <h3>Set priority for {selectedTicketIds.size} tickets</h3>
            </div>
            <div className="ownerModalBody" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(['P0', 'P1', 'P2', 'P3'] as SupportTicketPriority[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  className="ownerSupportSnoozeOption"
                  onClick={() => handleBulkPriority(p)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <span className={`ownerPriorityPip ownerPriorityPip--${p}`} />
                  <span>{p}</span>
                </button>
              ))}
            </div>
            <div className="ownerModalActions">
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--default"
                onClick={() => setShowBulkPriorityMenu(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showJumpModal && (
        <div className="ownerModalOverlay" onClick={() => setShowJumpModal(false)}>
          <div className="ownerModalDialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="ownerModalHeader">
              <h3>Jump to ticket by ID (#)</h3>
            </div>
            <div className="ownerModalBody">
              <input
                type="text"
                className="ownerSupportTagInput"
                placeholder="Enter ticket ID (e.g. abc123def)"
                value={jumpTicketIdInput}
                onChange={(e) => setJumpTicketIdInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleJumpToTicket(jumpTicketIdInput)
                  if (e.key === 'Escape') setShowJumpModal(false)
                }}
                autoFocus
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="ownerModalActions">
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--default"
                onClick={() => setShowJumpModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--primary"
                onClick={() => handleJumpToTicket(jumpTicketIdInput)}
                disabled={!jumpTicketIdInput.trim()}
              >
                Go to ticket
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

